/**
 * ============================================================================
 * CREATE MEMBER PAGE - Register New Team Member/User (WITH LOADING & ERROR HANDLING)
 * ============================================================================
 * 
 * This page allows admins to create new team members by:
 * - Entering member details (name, email, roll number, etc.)
 * - Uploading profile image
 * - Selecting team assignment
 * - Choosing class and year
 * 
 * BACKEND INTEGRATION:
 * ----------------------------------------------------------------------------
 * 
 * 📝 PRIMARY ENDPOINT:
 * POST /auth/register
 * 
 * Request Body (form data to send):
 * {
 *   "name": "John Doe",
 *   "email": "john@example.com",
 *   "rollNumber": "CSE2021001",
 *   "class": "CSE1",
 *   "year": "2nd Year",
 *   "team": "Tech",
 *   "profileImage": "base64_string_or_file_upload",
 *   "password": "generated_or_default_password",  // May need to add this
 *   "role": "member"  // or "admin" based on permissions
 * }
 * 
 * Expected Response:
 * {
 *   "success": true,
 *   "data": {
 *     "user": { id, name, email, ... },
 *     "message": "User registered successfully"
 *   }
 * }
 * 
 * ERROR HANDLING:
 * - Validation errors (email already exists, missing fields)
 * - Network errors
 * - Server errors
 * - Authentication errors (token expired)
 * 
 * STATE MANAGEMENT NEEDED:
 * - Add loading state for API call
 * - Add error state for displaying errors
 * - Add success message state
 * 
 * AFTER SUCCESSFUL CREATION:
 * - Navigate to /dashboard or /member/:id
 * - Show success notification
 * - Clear form data
 * 
 * ============================================================================
 */
import React, { useState, useEffect } from "react";
import { invalidateCache } from "../services/apiService";
import Loader from "../components/Loader";

export default function CreateMember({ isDarkMode, toggleDarkMode }) {

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  /**
   * Form data state
   * TODO: This will be sent to POST /auth/register
   * Maps to user registration fields in backend
   */
  const [formData, setFormData] = useState({
    name: "",
    rollNumber: "",
    email: "",
    class: "",           // Maps to user's class (CSE1, CSE2, etc.)
    year: "",            // Maps to user's academic year
    profileImage: "",    // Base64 encoded image or file upload
  });

  /**
   * Selected team state
   * Defaulted to "Tech"
   */
  const [selectedTeam, setSelectedTeam] = useState(null);

  /**
   * Image preview for profile picture
   * Shows preview before upload
   */
  const [imagePreview, setImagePreview] = useState("");

  /**
   * Dropdown state management
   * Controls custom dropdown visibility
   */
  const [classDropdownOpen, setClassDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);

  // NEW: Add states for backend integration:
  const [isLoading, setIsLoading] = useState(false); // API call in progress
  // NEW: Separate error states for each field
  const [nameError, setNameError] = useState("");
  const [rollNumberError, setRollNumberError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [classError, setClassError] = useState("");
  const [yearError, setYearError] = useState("");
  const [teamError, setTeamError] = useState("");
  const [generalError, setGeneralError] = useState(null);

  // Window resize listener for responsive state updates
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Close dropdowns when clicking outside
   * Improves UX by auto-closing dropdowns on outside clicks
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (classDropdownOpen && !event.target.closest('[data-class-dropdown]')) {
        setClassDropdownOpen(false);
      }
      if (yearDropdownOpen && !event.target.closest('[data-year-dropdown]')) {
        setYearDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [classDropdownOpen, yearDropdownOpen]);

  useEffect(() => {
    if (generalError && typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [generalError]);

  // ============================================================================
  // CONSTANTS - Static Data
  // ============================================================================

  /**
   * Available class options
   * Maps to backend user.class field
   */
  const classes = [
    { value: "CSE1", label: "CSE1", icon: "fa-book", emoji: "📚" },
    { value: "CSE2", label: "CSE2", icon: "fa-book", emoji: "📚" },
    { value: "CSE3", label: "CSE3", icon: "fa-book", emoji: "📚" },
    { value: "CSE4", label: "CSE4", icon: "fa-book", emoji: "📚" },
    { value: "AIML", label: "AIML", icon: "fa-robot", emoji: "🤖" }
  ];

  /**
   * Available year options
   * Maps to backend user.year field
   */
  const years = [
    { value: "1st Year", label: "1st Year", icon: "fa-graduation-cap", emoji: "🎓", color: "#667eea" },
    { value: "2nd Year", label: "2nd Year", icon: "fa-graduation-cap", emoji: "🎓", color: "#764ba2" },
    { value: "3rd Year", label: "3rd Year", icon: "fa-graduation-cap", emoji: "🎓", color: "#f093fb" },
    { value: "4th Year", label: "4th Year", icon: "fa-graduation-cap", emoji: "🎓", color: "#4facfe" }
  ];

  /**
   * Available teams
   * Maps to backend user.team field
   */
  const PR_CONTENT_TEAM = "PR-Content";
  const teams = ["Management", "Tech", "Design", PR_CONTENT_TEAM, "Media", "Support"];

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Get team-specific colors for UI styling
   * @param {string} team - Team name
   * @returns {object} Color scheme for the team
   */
  const getTeamColor = (team) => {
    const colors = {
      Management: { bg: 'rgba(251, 191, 36, 0.2)', border: 'rgba(251, 191, 36, 0.5)', color: '#fbbf24' },
      Tech: { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.5)', color: '#3b82f6' },
  Design: { bg: 'rgba(168, 85, 247, 0.2)', border: 'rgba(168, 85, 247, 0.5)', color: '#a855f7' },
  [PR_CONTENT_TEAM]: { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 0.5)', color: '#22c55e' },
      Media: { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.5)', color: '#ef4444' },
      Support: { bg: 'rgba(14, 165, 233, 0.2)', border: 'rgba(14, 165, 233, 0.5)', color: '#0ea5e9' }
    };
    return colors[team] || colors.Tech;
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle profile image upload
   * Converts image to base64 for preview and storage
   * 
   * TODO: For backend integration:
   * - Either send base64 string in JSON
   * - OR use FormData with multipart/form-data
   * - Check backend upload requirements (file size, format, etc.)
   * 
   * @param {Event} e - File input change event
   */
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
        // NEW: Clear any previous image-related errors
        setGeneralError("");
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        setImagePreview(result);
        setFormData({ ...formData, profileImage: result });
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * Handle form submission - Create new member
   * 
   * TODO: INTEGRATE WITH BACKEND
   * ============================================================================
   * BACKEND ENDPOINT: POST /auth/register
   * ============================================================================
   * 
   * Implementation Steps:
   * 
   * 1. Prepare 
   *    const memberData = {
   *      name: formData.name,
   *      email: formData.email,
   *      rollNumber: formData.rollNumber,
   *      class: formData.class,
   *      year: formData.year,
   *      team: selectedTeam,
   *      profileImage: formData.profileImage,
   *      password: "default_password",  // Generate or set default
   *      role: "member"                  // or "admin" based on permissions
   *    };
   * 
   * 2. Make API call:
   *    setIsLoading(true);
   *    setError(null);
   *    
   *    try {
   *      const token = localStorage.getItem('token');
   *      const response = await fetch('YOUR_BACKEND_URL/auth/register', {
   *        method: 'POST',
   *        headers: {
   *          'Content-Type': 'application/json',
   *          'Authorization': `Bearer ${token}`
   *        },
   *        body: JSON.stringify(memberData)
   *      });
   * 
   *      const data = await response.json();
   * 
   *      if (!response.ok) {
   *        throw new Error(data.message || 'Registration failed');
   *      }
   * 
   *      // Success handling
   *      setSuccessMessage('Member created successfully!');
   *      setTimeout(() => navigate('/dashboard'), 1500);
   * 
   *    } catch (error) {
   *      setError(error.message);
   *    } finally {
   *      setIsLoading(false);
   *    }
   * 
   * 3. Handle validation errors from backend
   * 4. Show success/error messages to user
   * 5. Redirect to dashboard or member profile on success
   * 
   * ============================================================================
   * 
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Clear previous errors and success message
    setNameError("");
    setRollNumberError("");
    setEmailError("");
    setClassError("");
    setYearError("");
    setTeamError("");
    setGeneralError(null);
    setIsLoading(true); // Set loading to true when starting the request

    let hasError = false;

    // Validation checks
    if (!formData.name.trim()) {
      setNameError("Please enter the member's full name.");
      hasError = true;
    }
    if (!formData.rollNumber.trim()) {
      setRollNumberError("Please enter the roll number.");
      hasError = true;
    }
    if (!formData.email.trim()) {
      setEmailError("Please enter an email address.");
      hasError = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setEmailError("Please enter a valid email address.");
        hasError = true;
      }
    }
    if (!formData.class) {
      setClassError("Please select a class.");
      hasError = true;
    }
    if (!formData.year) {
      setYearError("Please select an academic year.");
      hasError = true;
    }
    if (!selectedTeam) {
      setTeamError("Please select a team for the member.");
      hasError = true;
    }

    if (hasError) {
      setIsLoading(false); // Stop loading if validation fails
      return;
    }

    const { name, rollNumber, email, class: studentClass, year, profileImage } = formData;

    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!accessToken || !refreshToken) {
      setGeneralError("Authorization missing. Please log in as admin.");
      setIsLoading(false); // Stop loading if auth is missing
      return;
    }

    const createMemberRequest = async (token) => {
      const formDataToSend = new FormData();
      formDataToSend.append("fullname", name);
      formDataToSend.append("email", email);
      formDataToSend.append("classes", studentClass);
      formDataToSend.append("roll_no", rollNumber);
      formDataToSend.append("year", year);
      formDataToSend.append("team", selectedTeam.toLowerCase());
      if (profileImage && profileImage.startsWith("")) {
        const res = await fetch(profileImage);
        const blob = await res.blob();
        formDataToSend.append("avatar", blob, "profile.png");
      }
      // formDataToSend.append("password", "defaultPassword123"); // Add default password if required by backend
      const response = await fetch(
        "https://taskscheduler-backend.onrender.com/api/v1/auth/register", // Ensure no trailing spaces
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            // Do NOT set 'Content-Type' header when using FormData with fetch,
            // the browser sets it automatically with the correct boundary
          },
          body: formDataToSend,
        }
      );
      return response;
    };

    try {
      let response = await createMemberRequest(accessToken);

      // If token expired → try refresh
      if (response.status === 401 || response.status === 403) {
        console.warn("Access token expired. Attempting refresh...");
        const refreshResponse = await fetch(
          "https://taskscheduler-backend.onrender.com/api/v1/auth/refresh", // Ensure no trailing spaces
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken }),
          }
        );
        const refreshData = await refreshResponse.json();
        if (!refreshResponse.ok) {
          throw new Error(refreshData.message || "Session expired. Please log in again.");
        }
        localStorage.setItem("accessToken", refreshData.accessToken);
        // Retry with new token
        response = await createMemberRequest(refreshData.accessToken);
      }

      const data = await response.json();
      if (!response.ok) {
        // Handle different error statuses from the backend
        let errorMessage = data.message || "Failed to create member.";
        if (response.status === 400) {
          // Likely validation errors from the backend, could be specific fields
          // Example: if backend returns { message: "Email already exists" }
          // You could parse this and set setEmailError("...") if needed
          // For now, treat as general error
          errorMessage = data.message || "Invalid input provided.";
        } else if (response.status === 401 || response.status === 403) {
          // Shouldn't happen if refresh worked, but handle anyway
          errorMessage = "Unauthorized access. Please log in again.";
        } else if (response.status === 409) { // Assuming 409 Conflict for duplicate email
          // NEW: Check if it's specifically an email conflict and set field error
          if (data.message && data.message.toLowerCase().includes("email")) {
              setEmailError(data.message);
          } else {
              errorMessage = data.message || "A user with this email already exists.";
          }
        } else if (response.status >= 500) {
          // Server error
          errorMessage = "Server error. Please try again later.";
        }
        throw new Error(errorMessage);
      }

      // Show success alert
      console.log('✅ Member created successfully, invalidating members cache');
      // Invalidate members cache so dashboard will fetch fresh data
      invalidateCache.members();
      
      alert("Member created successfully");
      // Clear form data if needed
      setFormData({
        name: "",
        rollNumber: "",
        email: "",
        class: "",
        year: "",
        profileImage: "",
      });
      setSelectedTeam(null);
      setImagePreview(""); // Clear image preview
      // NEW: Clear team error after successful creation
      setTeamError("");
      // NEW: Navigate after a short delay to show the toast

    } catch (err) {
      console.error("Error creating member:", err);
      // Set the error message to be displayed
      setGeneralError(err.message || "An unexpected error occurred.");
    } finally {
      // Always stop the loading state, whether successful or failed
      setIsLoading(false);
    }
  };

  const themeBg = isDarkMode 
    ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    : '#ffffff';
  const themeText = isDarkMode ? 'rgba(255, 255, 255, 0.9)' : '#2c3e50';
  const themeBorder = isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)';
  const themeInputBg = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 1)';
  const themeInputBorder = isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)';

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      padding: 0,
      background: themeBg,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      position: 'relative',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      <div className="create-member-form" style={{
        width: '100%',
        maxWidth: windowWidth <= 768 ? '100%' : '42rem',
        animation: 'pageEnter 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        margin: '0 auto',
        boxSizing: 'border-box'
      }}>
        {generalError && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: windowWidth <= 768 ? '1.25rem' : '1.75rem',
            padding: windowWidth <= 768 ? '0 0.75rem' : '0 1.5rem',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: windowWidth <= 480 ? '0.85rem 1.35rem' : '1rem 2.25rem',
              borderRadius: windowWidth <= 480 ? '1rem' : '1.5rem',
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(185, 28, 28, 0.35) 100%)'
                : 'linear-gradient(135deg, rgba(254, 202, 202, 0.9) 0%, rgba(248, 113, 113, 0.9) 100%)',
              color: isDarkMode ? '#fee2e2' : '#7f1d1d',
              fontWeight: 600,
              letterSpacing: windowWidth <= 480 ? '0.015em' : '0.03em',
              fontSize: windowWidth <= 480 ? '0.95rem' : '1.1rem',
              boxShadow: isDarkMode
                ? '0 12px 30px rgba(239, 68, 68, 0.25)'
                : '0 12px 30px rgba(248, 113, 113, 0.25)',
              backdropFilter: 'blur(8px)',
              border: `1px solid ${isDarkMode ? 'rgba(248, 113, 113, 0.4)' : 'rgba(248, 113, 113, 0.5)'}`,
              width: '100%',
              maxWidth: windowWidth <= 768 ? '100%' : '32rem',
              textAlign: 'center',
              gap: '0.75rem',
              animation: 'errorSlideDown 0.45s ease-out, errorGlow 3s ease-in-out 0.45s infinite alternate'
            }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: windowWidth <= 480 ? '1rem' : '1.15rem' }}></i>
              <span>{generalError}</span>
            </div>
          </div>
        )}
        <div 
          className={isDarkMode ? 'glass-card-dark' : 'glass-card'}
          style={{
            borderRadius: windowWidth <= 768 ? '1rem' : '1.5rem',
            padding: windowWidth <= 768 ? '1.5rem' : '2rem',
            border: `1px solid ${themeBorder}`,
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            marginBottom: '2rem',
            flexWrap: 'wrap',
            textAlign: 'center'
          }}>
            <i className="fas fa-user-plus" style={{ fontSize: windowWidth <= 768 ? '1.75rem' : '2.25rem', color: '#00d9ff' }}></i>
            <h1 style={{
              fontSize: windowWidth <= 480 ? '1.35rem' : (windowWidth <= 768 ? '1.65rem' : '2.25rem'),
              fontWeight: '700',
              color: themeText,
              margin: 0
            }}>
              Create New Member
            </h1>
          </div>
          <form onSubmit={handleSubmit} style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '1.5rem',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            {/* Profile Image Upload — Next-Gen Glassmorphic Card */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginBottom: '1.75rem',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <label style={{ 
                cursor: 'pointer', 
                width: '100%',
                display: 'block'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <div style={{
                  width: '100%',
                  padding: window.innerWidth <= 480 ? '1.25rem 1rem' : '1.5rem 1.75rem',
                  borderRadius: '1.25rem',
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(238, 242, 255, 0.9) 100%)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: `2px dashed ${imagePreview ? (isDarkMode ? '#818cf8' : '#667eea') : (isDarkMode ? 'rgba(129, 140, 248, 0.4)' : 'rgba(102, 126, 234, 0.35)')}`,
                  display: 'flex',
                  flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1.25rem',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  boxShadow: isDarkMode 
                    ? '0 10px 30px rgba(0, 0, 0, 0.25)'
                    : '0 10px 30px rgba(102, 126, 234, 0.08)',
                  boxSizing: 'border-box'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = isDarkMode 
                    ? '0 15px 35px rgba(102, 126, 234, 0.25)' 
                    : '0 15px 35px rgba(102, 126, 234, 0.18)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = imagePreview ? (isDarkMode ? '#818cf8' : '#667eea') : (isDarkMode ? 'rgba(129, 140, 248, 0.4)' : 'rgba(102, 126, 234, 0.35)');
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isDarkMode 
                    ? '0 10px 30px rgba(0, 0, 0, 0.25)'
                    : '0 10px 30px rgba(102, 126, 234, 0.08)';
                }}
                >
                  {/* Left: Glowing Avatar Orb or Preview */}
                  <div style={{
                    position: 'relative',
                    flexShrink: 0
                  }}>
                    {imagePreview ? (
                      <div style={{
                        position: 'relative',
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        padding: '3px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
                      }}>
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }} 
                        />
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          background: '#22c55e',
                          border: '2px solid white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.75rem',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                        }}>
                          <i className="fas fa-check"></i>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        position: 'relative',
                        width: '76px',
                        height: '76px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px solid ${isDarkMode ? 'rgba(129, 140, 248, 0.4)' : 'rgba(102, 126, 234, 0.3)'}`,
                        boxShadow: '0 8px 20px rgba(102, 126, 234, 0.15)'
                      }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.25rem',
                          boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)'
                        }}>
                          <i className="fas fa-cloud-upload-alt"></i>
                        </div>
                        <div style={{
                          position: 'absolute',
                          bottom: '-2px',
                          right: '-2px',
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: '#3b82f6',
                          border: '2px solid white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.65rem',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                        }}>
                          <i className="fas fa-plus"></i>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right / Content Text */}
                  <div style={{
                    textAlign: window.innerWidth <= 480 ? 'center' : 'left',
                    flex: 1
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: window.innerWidth <= 480 ? 'center' : 'flex-start',
                      gap: '0.5rem',
                      marginBottom: '0.35rem'
                    }}>
                      <h3 style={{
                        fontSize: '1rem',
                        fontWeight: '700',
                        color: themeText,
                        margin: 0
                      }}>
                        {imagePreview ? 'Member Photo Uploaded' : 'Upload Member Photo'}
                      </h3>
                      {imagePreview && (
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#22c55e',
                          background: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '12px',
                          border: '1px solid rgba(34, 197, 94, 0.3)'
                        }}>
                          Ready
                        </span>
                      )}
                    </div>

                    <p style={{
                      fontSize: '0.8125rem',
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : '#64748b',
                      margin: '0 0 0.625rem 0',
                      lineHeight: '1.4'
                    }}>
                      {imagePreview 
                        ? 'Click anywhere on this card if you wish to change or replace the photo.' 
                        : 'Click to browse or drag & drop high-resolution member avatar image.'}
                    </p>

                    {/* File Format Pill Badges */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: window.innerWidth <= 480 ? 'center' : 'flex-start',
                      gap: '0.5rem',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        color: isDarkMode ? '#a78bfa' : '#667eea',
                        background: isDarkMode ? 'rgba(167, 139, 250, 0.12)' : 'rgba(102, 126, 234, 0.1)',
                        border: `1px solid ${isDarkMode ? 'rgba(167, 139, 250, 0.25)' : 'rgba(102, 126, 234, 0.2)'}`,
                        padding: '0.2rem 0.6rem',
                        borderRadius: '20px'
                      }}>
                        PNG, JPG, WEBP, GIF
                      </span>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#94a3b8',
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
                        padding: '0.2rem 0.6rem',
                        borderRadius: '20px'
                      }}>
                        Max 5MB
                      </span>
                    </div>
                  </div>
                </div>
              </label>
              {/* Add CSS animations */}
              <style>{`
                @keyframes pulse {
                  0%, 100% {
                    opacity: 0.6;
                    transform: scale(1);
                  }
                  50% {
                    opacity: 0.3;
                    transform: scale(1.1);
                  }
                }
                @keyframes bounce {
                  0%, 100% {
                    transform: translateY(0);
                  }
                  50% {
                    transform: translateY(-10px);
                  }
                }
                @keyframes sparkle {
                  0%, 100% {
                    opacity: 0.3;
                    transform: scale(1);
                  }
                  50% {
                    opacity: 1;
                    transform: scale(1.5);
                  }
                }
                @keyframes scaleIn {
                  0% {
                    opacity: 0;
                    transform: translateY(-50%) scale(0);
                  }
                  100% {
                    opacity: 1;
                    transform: translateY(-50%) scale(1);
                  }
                }
                @keyframes slideIn {
                  0% {
                    opacity: 0;
                    transform: translateX(-10px);
                  }
                  100% {
                    opacity: 1;
                    transform: translateX(0);
                  }
                }
                @keyframes successSlideDown {
                  0% {
                    opacity: 0;
                    transform: translateY(-18px) scale(0.98);
                  }
                  60% {
                    opacity: 1;
                    transform: translateY(3px) scale(1.02);
                  }
                  100% {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                  }
                }
                @keyframes successGlow {
                  0% {
                    box-shadow: 0 12px 30px rgba(16, 185, 129, 0.18);
                  }
                  100% {
                    box-shadow: 0 14px 36px rgba(16, 185, 129, 0.32);
                  }
                }
                @keyframes modalBackdropFade {
                  from { opacity: 0; }
                  to { opacity: 1; }
                }
                @keyframes modalPop {
                  0% {
                    opacity: 0;
                    transform: scale(0.9) translateY(20px);
                  }
                  60% {
                    opacity: 1;
                    transform: scale(1.02) translateY(-6px);
                  }
                  100% {
                    opacity: 1;
                    transform: scale(1) translateY(0);
                  }
                }
                @keyframes errorSlideDown {
                  0% {
                    opacity: 0;
                    transform: translateY(-16px) scale(0.98);
                  }
                  60% {
                    opacity: 1;
                    transform: translateY(4px) scale(1.02);
                  }
                  100% {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                  }
                }
                @keyframes errorGlow {
                  0% {
                    box-shadow: 0 12px 30px rgba(248, 113, 113, 0.22);
                  }
                  100% {
                    box-shadow: 0 14px 34px rgba(248, 113, 113, 0.34);
                  }
                }
                @keyframes fadeIn {
                  from { opacity: 0; }
                  to { opacity: 1; }
                }
                @keyframes slideInFromTop {
                  from {
                    transform: translateY(-20px);
                    opacity: 0;
                  }
                  to {
                    transform: translateY(0);
                    opacity: 1;
                  }
                }
                @keyframes slideInFromBottomLeft {
                  from {
                    transform: translate(-20px, 20px);
                    opacity: 0;
                  }
                  to {
                    transform: translate(0, 0);
                    opacity: 1;
                  }
                }
                @keyframes fadeOut {
                  from { opacity: 1; }
                  to { opacity: 0; }
                }
                @keyframes shakeError {
                  0%, 100% { transform: translateX(0); }
                  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
                  20%, 40%, 60%, 80% { transform: translateX(5px); }
                }
                label:hover .image-overlay {
                  opacity: 1 !important;
                }
                input::placeholder {
                  color: ${isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)'};
                  transition: opacity 0.3s ease;
                }
                input:focus::placeholder {
                  opacity: 0.6;
                }
              `}</style>
            </div>

            {/* Name & Roll Number - Two Column Grid */}
            <div className="create-member-grid" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', 
              gap: '1.5rem',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {/* Name */}
              <div style={{ width: '100%', boxSizing: 'border-box' }}>
                <label style={{
                  display: 'block',
                  color: themeText,
                  marginBottom: '0.625rem',
                  fontWeight: '600',
                  fontSize: window.innerWidth <= 375 ? '0.8125rem' : (window.innerWidth <= 480 ? '0.875rem' : '0.9375rem')
                }}>
                  Full Name *
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (nameError) setNameError(""); // Clear error on change
                    }}
                    style={{
                      width: '100%',
                      height: window.innerWidth <= 375 ? '3rem' : '3.25rem',
                      padding: window.innerWidth <= 375 ? '0 1rem' : '0 3rem 0 1rem',
                      background: formData.name 
                        ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                        : themeInputBg,
                      backdropFilter: 'blur(10px)',
                      border: `2px solid ${nameError ? (isDarkMode ? '#ef4444' : '#dc2626') : themeInputBorder}`, // Red border on error
                      borderRadius: window.innerWidth <= 375 ? '0.625rem' : '0.75rem',
                      color: themeText,
                      fontSize: window.innerWidth <= 375 ? '0.875rem' : '0.9375rem',
                      outline: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: nameError ? '0 0 0 4px rgba(239, 68, 68, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.1)', // Red glow on error
                      fontWeight: formData.name ? '500' : '400',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    placeholder="Enter full name"
                    required
                    onFocus={(e) => {
                      if (!nameError) { // Only apply if not in error state
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.08)';
                      }
                    }}
                    onBlur={(e) => {
                      if (!nameError) { // Only apply if not in error state
                          e.target.style.borderColor = themeInputBorder;
                          e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.background = formData.name 
                            ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                            : themeInputBg;
                      }
                    }}
                  />
                  {formData.name && !nameError && ( // Only show check if no error
                    <i className="fas fa-check-circle" style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#22c55e',
                      fontSize: '1rem',
                      pointerEvents: 'none',
                      animation: 'scaleIn 0.3s ease-out',
                      zIndex: 2
                    }}></i>
                  )}
                </div>
                {/* NEW: Field-specific error message for Name - Matching Login Page Style */}
                {nameError && (
                  <div className="field-error-message animate__animated animate__shakeX" style={{
                    padding: '0.75rem 1rem',
                    background: isDarkMode ? '#374151' : '#dc2626', // Solid dark red background
                    borderRadius: '0.5rem',
                    color: 'white', // White text
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Subtle shadow
                    animation: 'shakeError 0.3s ease-in-out'
                  }}>
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{nameError}</span>
                  </div>
                )}
              </div>
              {/* Roll Number */}
              <div style={{ width: '100%', boxSizing: 'border-box' }}>
                <label style={{
                  display: 'block',
                  color: themeText,
                  marginBottom: '0.625rem',
                  fontWeight: '600',
                  fontSize: window.innerWidth <= 375 ? '0.8125rem' : (window.innerWidth <= 480 ? '0.875rem' : '0.9375rem')
                }}>
                  Roll Number *
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="text"
                    value={formData.rollNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, rollNumber: e.target.value.toUpperCase() })
                    }
                    style={{
                      width: '100%',
                      height: window.innerWidth <= 375 ? '3rem' : '3.25rem',
                      padding: window.innerWidth <= 375 ? '0 1rem' : '0 3rem 0 1.25rem',
                      background: formData.rollNumber 
                        ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                        : themeInputBg,
                      backdropFilter: 'blur(10px)',
                      border: `2px solid ${rollNumberError ? (isDarkMode ? '#ef4444' : '#dc2626') : themeInputBorder}`, // Red border on error
                      borderRadius: window.innerWidth <= 375 ? '0.625rem' : '0.75rem',
                      color: themeText,
                      fontSize: window.innerWidth <= 375 ? '0.875rem' : '0.9375rem',
                      outline: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: rollNumberError ? '0 0 0 4px rgba(239, 68, 68, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.1)', // Red glow on error
                      fontWeight: formData.rollNumber ? '600' : '400',
                      letterSpacing: formData.rollNumber ? '0.5px' : 'normal',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    placeholder="e.g..16900124169"
                    required
                    onFocus={(e) => {
                      if (!rollNumberError) { // Only apply if not in error state
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.08)';
                      }
                    }}
                    onBlur={(e) => {
                      if (!rollNumberError) { // Only apply if not in error state
                          e.target.style.borderColor = themeInputBorder;
                          e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.background = formData.rollNumber 
                            ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                            : themeInputBg;
                      }
                    }}
                  />
                  {formData.rollNumber && !rollNumberError && ( // Only show check if no error
                    <i className="fas fa-check-circle" style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#22c55e',
                      fontSize: '1rem',
                      pointerEvents: 'none',
                      animation: 'scaleIn 0.3s ease-out',
                      zIndex: 2
                    }}></i>
                  )}
                </div>
                {/* NEW: Field-specific error message for Roll Number - Matching Login Page Style */}
                {rollNumberError && (
                  <div className="field-error-message animate__animated animate__shakeX" style={{
                    padding: '0.75rem 1rem',
                    background: isDarkMode ? '#374151' : '#dc2626', // Solid dark red background
                    borderRadius: '0.5rem',
                    color: 'white', // White text
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Subtle shadow
                    animation: 'shakeError 0.3s ease-in-out'
                  }}>
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{rollNumberError}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label style={{
                display: 'block',
                color: themeText,
                marginBottom: '0.625rem',
                fontWeight: '600',
                fontSize: window.innerWidth <= 375 ? '0.8125rem' : (window.innerWidth <= 480 ? '0.875rem' : '0.9375rem')
              }}>
                Email Address *
              </label>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (emailError) setEmailError(""); // Clear error on change
                  }}
                  style={{
                    width: '100%',
                    height: window.innerWidth <= 375 ? '3rem' : '3.25rem',
                    padding: window.innerWidth <= 375 ? '0 1rem' : '0 3rem 0 1rem',
                    background: formData.email 
                      ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                      : themeInputBg,
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${emailError ? (isDarkMode ? '#ef4444' : '#dc2626') : themeInputBorder}`, // Red border on error
                    borderRadius: window.innerWidth <= 375 ? '0.625rem' : '0.75rem',
                    color: themeText,
                    fontSize: window.innerWidth <= 375 ? '0.875rem' : '0.9375rem',
                    outline: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: emailError ? '0 0 0 4px rgba(239, 68, 68, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.1)', // Red glow on error
                    fontWeight: formData.email ? '500' : '400',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  placeholder="e.g., student@example.com"
                  required
                  onFocus={(e) => {
                    if (!emailError) { // Only apply if not in error state
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.08)';
                    }
                  }}
                  onBlur={(e) => {
                    if (!emailError) { // Only apply if not in error state
                        e.target.style.borderColor = themeInputBorder;
                        e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.background = formData.email 
                          ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                          : themeInputBg;
                    }
                  }}
                />
                {formData.email && !emailError && ( // Only show check if no error
                  <i className="fas fa-check-circle" style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#22c55e',
                    fontSize: '1rem',
                    pointerEvents: 'none',
                    animation: 'scaleIn 0.3s ease-out',
                    zIndex: 2
                  }}></i>
                )}
              </div>
              {/* NEW: Field-specific error message for Email - Matching Login Page Style */}
              {emailError && (
                  <div className="field-error-message animate__animated animate__shakeX" style={{
                    padding: '0.75rem 1rem',
                    background: isDarkMode ? '#374151' : '#dc2626', // Solid dark red background
                    borderRadius: '0.5rem',
                    color: 'white', // White text
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Subtle shadow
                    animation: 'shakeError 0.3s ease-in-out'
                  }}>
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{emailError}</span>
                  </div>
              )}
            </div>

            {/* Class & Year */}
            <div className="create-member-grid" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', 
              gap: '1.5rem',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {/* Class Dropdown */}
              <div data-class-dropdown style={{ position: 'relative' }}>
                <label style={{
                  display: 'block',
                  color: themeText,
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  fontSize: window.innerWidth <= 375 ? '0.8125rem' : '0.9375rem'
                }}>
                  Class *
                </label>
                <div
                  onClick={() => {
                    setClassDropdownOpen(!classDropdownOpen);
                    setYearDropdownOpen(false);
                    if (classError) setClassError(""); // Clear error on click
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: themeInputBg,
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${classError ? (isDarkMode ? '#ef4444' : '#dc2626') : (classDropdownOpen ? '#667eea' : themeInputBorder)}`, // Red border on error
                    borderRadius: '0.75rem',
                    fontSize: '0.9375rem',
                    color: formData.class ? themeText : (isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.3s ease',
                    boxShadow: classError ? '0 0 0 4px rgba(239, 68, 68, 0.1)' : (classDropdownOpen ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'), // Red glow on error
                    fontWeight: formData.class ? '500' : '400'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {formData.class ? (
                      <>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.875rem',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          flexShrink: 0
                        }}>
                          <i className={`fas ${classes.find(s => s.value === formData.class)?.icon || 'fa-book'}`}></i>
                        </div>
                        <span>{formData.class}</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-layer-group" style={{ color: '#667eea' }}></i>
                        <span>Select Class</span>
                      </>
                    )}
                  </span>
                  <i 
                    className={`fas fa-chevron-${classDropdownOpen ? 'up' : 'down'}`}
                    style={{ 
                      color: classError ? (isDarkMode ? '#ef4444' : '#dc2626') : '#667eea', // Red chevron on error
                      transition: 'transform 0.3s ease',
                      fontSize: '0.875rem'
                    }}
                  ></i>
                </div>
                {/* Class Dropdown Menu */}
                {classDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.5rem',
                    background: isDarkMode ? 'rgba(26, 26, 46, 0.95)' : 'white',
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
                    borderRadius: '0.75rem',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'slideDown 0.3s ease-out'
                  }}>
                    <div style={{
                      padding: '0.5rem',
                      maxHeight: '280px',
                      overflowY: 'auto'
                    }}>
                      {classes.map((classItem) => {
                        const isSelected = formData.class === classItem.value;
                        return (
                          <div
                            key={classItem.value}
                            onClick={() => {
                              setFormData({ ...formData, class: classItem.value });
                              setClassDropdownOpen(false);
                            }}
                            style={{
                              padding: '0.875rem 1rem',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              background: isSelected 
                                ? (isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)')
                                : 'transparent',
                              transition: 'all 0.2s ease',
                              marginBottom: '0.25rem'
                            }}
                            onMouseOver={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.05)';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: classItem.value === 'AIML' 
                                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.875rem',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                              flexShrink: 0
                            }}>
                              <i className={`fas ${classItem.icon}`}></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '600',
                                color: themeText,
                                fontSize: '0.9375rem'
                              }}>
                                {classItem.emoji} {classItem.label}
                              </div>
                            </div>
                            {isSelected && (
                              <i className="fas fa-check" style={{ color: '#667eea', fontSize: '0.875rem' }}></i>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* NEW: Field-specific error message for Class - Matching Login Page Style */}
                {classError && (
                  <div className="field-error-message animate__animated animate__shakeX" style={{
                    padding: '0.75rem 1rem',
                    background: isDarkMode ? '#374151' : '#dc2626', // Solid dark red background
                    borderRadius: '0.5rem',
                    color: 'white', // White text
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Subtle shadow
                    animation: 'shakeError 0.3s ease-in-out'
                  }}>
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{classError}</span>
                  </div>
                )}
              </div>
              {/* Year Dropdown */}
              <div data-year-dropdown style={{ position: 'relative' }}>
                <label style={{
                  display: 'block',
                  color: themeText,
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  fontSize: window.innerWidth <= 375 ? '0.8125rem' : '0.9375rem'
                }}>
                  Year *
                </label>
                <div
                  onClick={() => {
                    setYearDropdownOpen(!yearDropdownOpen);
                    setClassDropdownOpen(false);
                    if (yearError) setYearError(""); // Clear error on click
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: themeInputBg,
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${yearError ? (isDarkMode ? '#ef4444' : '#dc2626') : (yearDropdownOpen ? '#667eea' : themeInputBorder)}`, // Red border on error
                    borderRadius: '0.75rem',
                    fontSize: '0.9375rem',
                    color: formData.year ? themeText : (isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.3s ease',
                    boxShadow: yearError ? '0 0 0 4px rgba(239, 68, 68, 0.1)' : (yearDropdownOpen ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'), // Red glow on error
                    fontWeight: formData.year ? '500' : '400'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {formData.year ? (
                      <>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${years.find(y => y.value === formData.year)?.color || '#667eea'} 0%, ${years.find(y => y.value === formData.year)?.color || '#764ba2'} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.875rem',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          flexShrink: 0
                        }}>
                          <i className="fas fa-graduation-cap"></i>
                        </div>
                        <span>{formData.year}</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-graduation-cap" style={{ color: '#667eea' }}></i>
                        <span>Select Year</span>
                      </>
                    )}
                  </span>
                  <i 
                    className={`fas fa-chevron-${yearDropdownOpen ? 'up' : 'down'}`}
                    style={{ 
                      color: yearError ? (isDarkMode ? '#ef4444' : '#dc2626') : '#667eea', // Red chevron on error
                      transition: 'transform 0.3s ease',
                      fontSize: '0.875rem'
                    }}
                  ></i>
                </div>
                {/* Year Dropdown Menu */}
                {yearDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.5rem',
                    background: isDarkMode ? 'rgba(26, 26, 46, 0.95)' : 'white',
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
                    borderRadius: '0.75rem',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'slideDown 0.3s ease-out'
                  }}>
                    <div style={{
                      padding: '0.5rem',
                      maxHeight: '280px',
                      overflowY: 'auto'
                    }}>
                      {years.map((year) => {
                        const isSelected = formData.year === year.value;
                        return (
                          <div
                            key={year.value}
                            onClick={() => {
                              setFormData({ ...formData, year: year.value });
                              setYearDropdownOpen(false);
                            }}
                            style={{
                              padding: '0.875rem 1rem',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              background: isSelected 
                                ? (isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)')
                                : 'transparent',
                              transition: 'all 0.2s ease',
                              marginBottom: '0.25rem'
                            }}
                            onMouseOver={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.05)';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${year.color} 0%, ${year.color}dd 100%)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.875rem',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                              flexShrink: 0
                            }}>
                              <i className={`fas ${year.icon}`}></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '600',
                                color: themeText,
                                fontSize: '0.9375rem'
                              }}>
                                {year.emoji} {year.label}
                              </div>
                            </div>
                            {isSelected && (
                              <i className="fas fa-check" style={{ color: '#667eea', fontSize: '0.875rem' }}></i>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* NEW: Field-specific error message for Year - Matching Login Page Style */}
                {yearError && (
                  <div className="field-error-message animate__animated animate__shakeX" style={{
                    padding: '0.75rem 1rem',
                    background: isDarkMode ? '#374151' : '#dc2626', // Solid dark red background
                    borderRadius: '0.5rem',
                    color: 'white', // White text
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Subtle shadow
                    animation: 'shakeError 0.3s ease-in-out'
                  }}>
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{yearError}</span>
                  </div>
                )}
              </div>
            </div>



            {/* Team Selection — Fully Responsive Grid */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', boxSizing: 'border-box' }}>
              <label style={{
                display: 'block',
                color: themeText,
                fontWeight: '600',
                fontSize: windowWidth <= 480 ? '0.875rem' : '1rem'
              }}>
                Select Team <span style={{ color: '#ef4444' }}>*</span>
              </label>

              <div 
                className="team-selection"
                style={{
                  display: 'grid',
                  gridTemplateColumns: windowWidth <= 768 ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
                  gap: windowWidth <= 480 ? '0.875rem 0.5rem' : '1.25rem',
                  justifyItems: 'center',
                  alignItems: 'start',
                  padding: windowWidth <= 480 ? '1rem 0.5rem' : '1.5rem 1rem',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(102, 126, 234, 0.04)',
                  borderRadius: '1rem',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(102, 126, 234, 0.15)'}`,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {teams.map((team) => {
                  const teamColors = getTeamColor(team);
                  const isSelected = selectedTeam === team;
                  return (
                    <div 
                      key={team} 
                      style={{ 
                        textAlign: 'center', 
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: '100%',
                        gap: '0.5rem',
                        transition: 'transform 0.2s ease'
                      }}
                      onClick={() => {
                        setSelectedTeam(team);
                        if (teamError) setTeamError("");
                      }}
                    >
                      <div style={{
                        width: windowWidth <= 375 ? '3.25rem' : (windowWidth <= 480 ? '3.5rem' : '4rem'),
                        height: windowWidth <= 375 ? '3.25rem' : (windowWidth <= 480 ? '3.5rem' : '4rem'),
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: isSelected 
                          ? `linear-gradient(135deg, ${teamColors.color} 0%, ${teamColors.color}dd 100%)`
                          : (isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.95)'),
                        border: `2px solid ${isSelected ? teamColors.border : (isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)')}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                        boxShadow: isSelected 
                          ? `0 0 0 3px ${teamColors.border}40, 0 6px 18px ${teamColors.color}40`
                          : '0 2px 8px rgba(0, 0, 0, 0.08)',
                        position: 'relative'
                      }}>
                        <i className={`fas ${
                          team === 'Management' ? 'fa-crown' :
                          team === 'Tech' ? 'fa-microchip' :
                          team === 'Design' ? 'fa-palette' :
                          team === PR_CONTENT_TEAM ? 'fa-bullhorn' :
                          team === 'Media' ? 'fa-video' :
                          team === 'Support' ? 'fa-headset' :
                          'fa-users'
                        }`}
                          style={{ 
                            fontSize: windowWidth <= 375 ? '1.25rem' : (windowWidth <= 480 ? '1.35rem' : '1.5rem'), 
                            color: isSelected ? '#ffffff' : teamColors.color,
                            transition: 'color 0.2s ease'
                          }}
                        ></i>
                      </div>
                      <span style={{ 
                        color: isSelected 
                          ? teamColors.color 
                          : (isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)'), 
                        fontSize: windowWidth <= 375 ? '0.6875rem' : (windowWidth <= 480 ? '0.75rem' : '0.8125rem'), 
                        fontWeight: isSelected ? '700' : '500',
                        textAlign: 'center',
                        lineHeight: '1.2',
                        wordBreak: 'break-word'
                      }}>
                        {team}
                      </span>
                    </div>
                  );
                })}
              </div>

              {teamError && (
                <div className="field-error-message animate__animated animate__shakeX" style={{
                  padding: '0.75rem 1rem',
                  background: isDarkMode ? '#374151' : '#dc2626',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.875rem',
                  marginTop: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}>
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{teamError}</span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading} // NEW: Disable button when loading
              style={{
                width: '100%',
                padding: '1rem 2rem',
                background: isLoading // NEW: Slightly different background when loading
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' // Keep gradient
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Or adjust
                color: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                fontSize: '1.125rem',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer', // NEW: Change cursor when loading
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                opacity: isLoading ? 0.7 : 1, // NEW: Visual feedback for loading state
                display: 'flex', // NEW: Align content inside button
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem' // NEW: Space between text and spinner
              }}
              onMouseOver={(e) => {
                if (!isLoading) { // NEW: Only apply hover effect if not loading
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                }
              }}
              onMouseOut={(e) => {
                 if (!isLoading) { // NEW: Only apply hover effect if not loading
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                 }
              }}
            >
              {isLoading ? (
                <Loader variant="inline" size="sm" text="Creating..." isDarkMode={isDarkMode} />
              ) : (
                "Create Member"
              )}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
}